<template>
  <div class="embed-scripts-list-container">
    <transition name="fade">
      <div
        v-if="showNotificationFlag"
        class="notification"
        :class="notificationType"
      >
        {{ notificationMessage }}
      </div>
    </transition>

    <div class="list-header">
      <h2 class="list-title">
        {{ t('registration.embedScripts') || 'Embed Scripts' }}
      </h2>
      <button type="button" class="btn btn-primary" @click="openCreate">
        {{ t('registration.createEmbedScript') || 'Create Embed Script' }}
      </button>
    </div>

    <div v-if="embedScripts.length === 0" class="empty-state">
      <div class="empty-icon">🔗</div>
      <h3>{{ t('registration.noEmbedScripts') || 'No embed scripts yet' }}</h3>
      <p>
        {{
          t('registration.noEmbedScriptsDescription') ||
          'Create a universal embed script and link the races/cups it should expose.'
        }}
      </p>
      <button type="button" class="btn btn-primary" @click="openCreate">
        {{ t('registration.createEmbedScript') || 'Create Embed Script' }}
      </button>
    </div>

    <div v-else class="forms-grid">
      <table class="forms-table embed-scripts-table">
        <thead>
          <tr>
            <th>{{ t('general.name') || 'Name' }}</th>
            <th>{{ t('registration.linkedTargets') || 'Linked Targets' }}</th>
            <th>{{ t('general.status') || 'Status' }}</th>
            <th class="embed-column">{{ t('registration.embedCode') }}</th>
            <th>{{ t('general.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="script in embedScripts" :key="script.id">
            <td class="form-title-cell">
              <div class="script-name">{{ script.name }}</div>
              <div v-if="script.description" class="script-description">
                {{ script.description }}
              </div>
            </td>
            <td class="targets-cell">
              <ul v-if="script.targets && script.targets.length > 0" class="target-list">
                <li
                  v-for="target in script.targets"
                  :key="target.key"
                  class="target-list-item"
                >
                  {{ targetBadgeLabel(target) }}
                </li>
              </ul>
              <span v-else class="muted">
                {{ t('registration.noTargetsLinked') || 'No targets linked' }}
              </span>
            </td>
            <td>
              <span class="status-badge" :class="script.is_active ? 'active' : 'inactive'">
                {{
                  script.is_active
                    ? t('competitions.statusActive') || 'Active'
                    : t('competitions.statusInactive') || 'Inactive'
                }}
              </span>
            </td>
            <td class="embed-cell">
              <button type="button" class="btn-action" @click="openEmbedCode(script)">
                {{ t('registration.embedCode') }}
              </button>
            </td>
            <td>
              <div class="form-actions">
                <button type="button" class="btn-action" @click="openEdit(script)">
                  {{ t('general.edit') }}
                </button>
                <button
                  type="button"
                  class="btn-action-danger"
                  @click="deleteScript(script.id)"
                >
                  {{ t('general.delete') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <transition name="fade">
      <div
        v-if="showEditorModal"
        class="modal-overlay"
        @click="closeEditor"
      >
        <div class="modal-content editor-modal-content" @click.stop>
          <div class="modal-header">
            <h3>
              {{
                editingScriptId
                  ? t('registration.editEmbedScript') || 'Edit Embed Script'
                  : t('registration.createEmbedScript') || 'Create Embed Script'
              }}
            </h3>
            <button class="close-btn" @click="closeEditor">&times;</button>
          </div>

          <form class="modal-body modal-body-editor" @submit.prevent="saveScript">
            <div class="modal-intro">
              <p>
                {{
                  t('registration.noEmbedScriptsDescription') ||
                  'Create a universal embed script and choose which races and cups it exposes.'
                }}
              </p>
            </div>

            <section class="modal-section">
              <div class="modal-section-header">
                <h4 class="modal-section-title">
                  {{ t('registration.embedScriptName') || 'Embed script name' }}
                </h4>
              </div>
              <div class="modal-section-body">
                <div class="form-grid">
                  <div class="form-group">
                    <label for="embed-script-name">
                      {{ t('registration.embedScriptName') || 'Embed script name' }} *
                    </label>
                    <input
                      id="embed-script-name"
                      v-model.trim="scriptForm.name"
                      type="text"
                      required
                      :placeholder="
                        t('registration.embedScriptNamePlaceholder') ||
                        'Universal registrations'
                      "
                    />
                  </div>

                  <div class="form-group form-group-wide">
                    <label for="embed-script-description">
                      {{
                        t('registration.embedScriptDescription') ||
                        'Description'
                      }}
                    </label>
                    <textarea
                      id="embed-script-description"
                      v-model.trim="scriptForm.description"
                      rows="3"
                      :placeholder="
                        t('registration.embedScriptDescriptionPlaceholder') ||
                        'Shown only in the organizer dashboard'
                      "
                    />
                  </div>

                  <div class="form-group">
                    <label for="embed-script-target-label">
                      {{
                        t('registration.selectEmbedTargetLabel') ||
                        'Select Registration'
                      }}
                    </label>
                    <input
                      id="embed-script-target-label"
                      v-model.trim="scriptForm.select_target_label"
                      type="text"
                      :placeholder="
                        t('registration.selectEmbedTargetLabel') ||
                        'Select Registration'
                      "
                    />
                  </div>

                  <div class="form-group">
                    <label for="embed-script-target-placeholder">
                      {{
                        t('registration.selectEmbedTargetPlaceholder') ||
                        'Please select a race or cup...'
                      }}
                    </label>
                    <input
                      id="embed-script-target-placeholder"
                      v-model.trim="scriptForm.select_target_placeholder"
                      type="text"
                      :placeholder="
                        t('registration.selectEmbedTargetPlaceholder') ||
                        'Please select a race or cup...'
                      "
                    />
                  </div>
                </div>

                <label class="checkbox-row">
                  <input v-model="scriptForm.is_active" type="checkbox" />
                  <span>
                    {{
                      t('registration.embedScriptActive') ||
                      'Embed script is active'
                    }}
                  </span>
                </label>
              </div>
            </section>

            <section class="modal-section">
              <div class="modal-section-header">
                <h4 class="modal-section-title">
                  {{ t('registration.linkedTargets') || 'Linked Targets' }}
                </h4>
                <div class="modal-section-note">
                  {{
                    t('registration.embedScriptTargetNeedsForm') ||
                    'Assign a registration form first'
                  }}
                </div>
              </div>
              <div class="modal-section-body">
                <div v-if="orderedSelectedTargets.length > 0" class="selected-targets-panel">
                  <div class="selected-targets-header">
                    <h5 class="target-section-title">
                      {{
                        t('registration.embedScriptTargetOrder') ||
                        'Selected order'
                      }}
                    </h5>
                    <p class="selected-targets-note">
                      {{
                        t('registration.embedScriptTargetOrderHelp') ||
                        'This order is used in the public embed selector.'
                      }}
                    </p>
                  </div>
                  <div class="selected-targets-list">
                    <div
                      v-for="(target, index) in orderedSelectedTargets"
                      :key="target.key"
                      class="selected-target-row"
                    >
                      <span class="selected-target-index">{{ index + 1 }}</span>
                      <div class="selected-target-content">
                        <div class="selected-target-name">
                          {{ target.name }}
                        </div>
                        <div class="selected-target-meta">
                          {{ target.meta }}
                        </div>
                      </div>
                      <div class="selected-target-actions">
                        <button
                          type="button"
                          class="btn btn-order"
                          :disabled="index === 0"
                          :aria-label="
                            t('registration.moveTargetUp') || 'Move target up'
                          "
                          @click="moveTargetUp(index)"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          class="btn btn-order"
                          :disabled="index === orderedSelectedTargets.length - 1"
                          :aria-label="
                            t('registration.moveTargetDown') || 'Move target down'
                          "
                          @click="moveTargetDown(index)"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="targets-split">
                  <div class="target-section">
                    <h5 class="target-section-title">
                      {{ t('registration.linkCompetitions') || 'Linked races' }}
                    </h5>
                    <div v-if="linkableCompetitions.length === 0" class="muted">
                      {{
                        t('registration.noLinkableCompetitions') ||
                        'No races with registration forms available'
                      }}
                    </div>
                    <div v-else class="target-selection-table">
                      <div class="target-selection-head">
                        <span>{{ t('general.name') || 'Name' }}</span>
                        <span>{{ t('competitions.registrationForm') }}</span>
                      </div>
                      <label
                        v-for="competition in linkableCompetitions"
                        :key="`competition-${competition.id}`"
                        class="target-selection-row"
                      >
                        <span class="target-selection-control">
                          <input
                            type="checkbox"
                            :checked="isSelected('competition', competition.id)"
                            @change="toggleTarget('competition', competition.id)"
                          />
                        </span>
                        <span class="target-selection-name">
                          {{ competition.name }}
                        </span>
                        <span class="target-selection-meta">
                          {{ competitionTargetMeta(competition) }}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div class="target-section">
                    <h5 class="target-section-title">
                      {{ t('registration.linkCups') || 'Linked cups' }}
                    </h5>
                    <div v-if="linkableCups.length === 0" class="muted">
                      {{
                        t('registration.noLinkableCups') ||
                        'No cups with registration forms available'
                      }}
                    </div>
                    <div v-else class="target-selection-table">
                      <div class="target-selection-head">
                        <span>{{ t('general.name') || 'Name' }}</span>
                        <span>{{ t('cups.registrationForm') || 'Registration Form' }}</span>
                      </div>
                      <label
                        v-for="cup in linkableCups"
                        :key="`cup-${cup.id}`"
                        class="target-selection-row"
                      >
                        <span class="target-selection-control">
                          <input
                            type="checkbox"
                            :checked="isSelected('cup', cup.id)"
                            @change="toggleTarget('cup', cup.id)"
                          />
                        </span>
                        <span class="target-selection-name">
                          {{ cup.name }}
                        </span>
                        <span class="target-selection-meta">
                          {{ cupTargetMeta(cup) }}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div v-if="selectedTargets.length === 0" class="selection-warning">
                  {{
                    t('registration.embedScriptTargetRequired') ||
                    'Select at least one race or cup with an assigned registration form.'
                  }}
                </div>
              </div>
            </section>

            <div class="modal-footer">
              <button type="submit" class="btn modal-save-btn">
                {{ t('button.save') }}
              </button>
              <button type="button" class="btn modal-cancel-btn" @click="closeEditor">
                {{ t('button.cancel') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="showEmbedModal && selectedScript"
        class="modal-overlay"
        @click="closeEmbedModal"
      >
        <div class="modal-content embed-modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ t('registration.universalEmbedCode') || 'Universal Embed Code' }}</h3>
            <button class="close-btn" @click="closeEmbedModal">&times;</button>
          </div>

          <div class="modal-body">
            <div class="modal-intro modal-intro-compact">
              <h4>{{ selectedScript.name }}</h4>
              <p v-if="selectedScript.description" class="script-description">
                {{ selectedScript.description }}
              </p>
            </div>

            <section class="modal-section">
              <div class="modal-section-header">
                <h4 class="modal-section-title">
                  {{ t('registration.embedOptions') }}
                </h4>
              </div>
              <div class="modal-section-body">
                <div class="embed-options">
                  <label class="option-label option-row">
                    <input type="radio" v-model="embedMethod" value="script" />
                    <span>{{ t('registration.scriptEmbed') }}</span>
                  </label>
                  <label class="option-label option-row">
                    <input type="radio" v-model="embedMethod" value="container" />
                    <span>{{ t('registration.containerEmbed') }}</span>
                  </label>
                </div>

                <div v-if="embedMethod === 'container'" class="form-group form-group-inline-field">
                  <label for="embed-script-container">
                    {{ t('registration.containerId') }}
                  </label>
                  <input
                    id="embed-script-container"
                    v-model.trim="containerId"
                    type="text"
                    placeholder="my-registration-embed"
                  />
                </div>
              </div>
            </section>

            <section class="modal-section">
              <div class="modal-section-header">
                <h4 class="modal-section-title">
                  {{ t('registration.embedCode') }}
                </h4>
              </div>
              <div class="modal-section-body">
                <div class="code-container">
                  <pre class="code-block"><code>{{ generatedEmbedCode }}</code></pre>
                </div>
              </div>
            </section>

            <div class="modal-footer">
              <button type="button" class="btn modal-save-btn" @click="copyEmbedCode">
                {{ t('general.copy') }}
              </button>
              <button type="button" class="btn modal-cancel-btn" @click="closeEmbedModal">
                {{ t('button.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useNotification } from '@/composables/useNotification'

export default {
  name: 'EmbedScriptsList',
  setup() {
    const { t } = useI18n()
    const {
      showNotificationFlag,
      notificationMessage,
      notificationType,
      showNotification,
    } = useNotification()

    const embedScripts = ref([])
    const competitions = ref([])
    const cups = ref([])
    const registrationForms = ref([])

    const showEditorModal = ref(false)
    const showEmbedModal = ref(false)
    const editingScriptId = ref(null)
    const selectedScript = ref(null)
    const embedMethod = ref('script')
    const containerId = ref('my-registration-embed')

    const scriptForm = ref({
      name: '',
      description: '',
      select_target_label: '',
      select_target_placeholder: '',
      is_active: true,
      targets: [],
    })

    const selectedTargets = computed(() => scriptForm.value.targets || [])
    const isTargetActive = (target) => {
      const status = String(target?.status || '').toLowerCase()
      return Boolean(target?.registration_form_id) && status !== 'completed'
    }
    const linkableCompetitions = computed(() =>
      (competitions.value || []).filter(isTargetActive)
    )
    const linkableCups = computed(() => (cups.value || []).filter(isTargetActive))
    const availableTargetKeys = computed(() => {
      const keys = new Set()
      linkableCompetitions.value.forEach((competition) => {
        keys.add(`competition:${competition.id}`)
      })
      linkableCups.value.forEach((cup) => {
        keys.add(`cup:${cup.id}`)
      })
      return keys
    })
    const sanitizeTargets = (targets = []) =>
      targets.filter((target) =>
        availableTargetKeys.value.has(`${target.targetType}:${Number(target.targetId)}`)
      )

    const formNameById = computed(() => {
      const map = new Map()
      registrationForms.value.forEach((form) => {
        map.set(form.id, form.title || form.name || `#${form.id}`)
      })
      return map
    })
    const targetLookup = computed(() => {
      const map = new Map()
      ;(competitions.value || []).forEach((competition) => {
        map.set(`competition:${competition.id}`, {
          key: `competition:${competition.id}`,
          name: competition.name,
          meta: competitionTargetMeta(competition),
        })
      })
      ;(cups.value || []).forEach((cup) => {
        map.set(`cup:${cup.id}`, {
          key: `cup:${cup.id}`,
          name: cup.name,
          meta: cupTargetMeta(cup),
        })
      })
      return map
    })
    const orderedSelectedTargets = computed(() =>
      selectedTargets.value.map((target, index) => {
        const key = `${target.targetType}:${Number(target.targetId)}`
        const detail = targetLookup.value.get(key)
        return {
          ...target,
          index,
          key,
          name:
            detail?.name ||
            `${target.targetType === 'cup' ? 'Cup' : 'Race'} #${target.targetId}`,
          meta: detail?.meta || '',
        }
      })
    )

    const baseUrl = computed(() => process.env.VUE_APP_BASE_URL)

    const generatedEmbedCode = computed(() => {
      if (!selectedScript.value) return ''
      const organizerId =
        selectedScript.value.organizer_id || selectedScript.value.organizerId
      const scriptId = selectedScript.value.id

      if (embedMethod.value === 'script') {
        return `<!-- Universal Registration Embed -->
<script src="${baseUrl.value}/registration.js"><\/script>
<script>
  initUniversalRegistrationEmbed('${organizerId}', '${scriptId}');
<\/script>`
      }

      return `<!-- Universal Registration Container -->
<div id="${containerId.value}"></div>

<!-- Universal Registration Script -->
<script src="${baseUrl.value}/registration.js"><\/script>
<script>
  embedUniversalRegistrationForm('${containerId.value}', '${organizerId}', '${scriptId}');
<\/script>`
    })

    const normalizeScript = (script) => ({
      ...script,
      select_target_label:
        script.select_target_label || script.selectTargetLabel || '',
      select_target_placeholder:
        script.select_target_placeholder || script.selectTargetPlaceholder || '',
      is_active: Boolean(script.is_active),
      targets: Array.isArray(script.targets) ? script.targets : [],
    })

    const fetchEmbedScripts = async () => {
      const response = await axios.get('/registration/embed-scripts')
      embedScripts.value = (response.data || []).map(normalizeScript)
    }

    const fetchCompetitions = async () => {
      const response = await axios.get('/registration/competitions')
      competitions.value = response.data || []
    }

    const fetchCups = async () => {
      const response = await axios.get('/registration/cups')
      cups.value = response.data || []
    }

    const fetchRegistrationForms = async () => {
      const response = await axios.get('/registration/forms')
      registrationForms.value = response.data || []
    }

    const fetchAll = async () => {
      try {
        await Promise.all([
          fetchEmbedScripts(),
          fetchCompetitions(),
          fetchCups(),
          fetchRegistrationForms(),
        ])
      } catch (error) {
        console.error('Error fetching embed script data:', error)
        showNotification(
          t('registration.embedScriptsLoadFailed') ||
            'Failed to load embed scripts.',
          'error'
        )
      }
    }

    const resetForm = () => {
      scriptForm.value = {
        name: '',
        description: '',
        select_target_label: '',
        select_target_placeholder: '',
        is_active: true,
        targets: [],
      }
      editingScriptId.value = null
    }

    const openCreate = () => {
      resetForm()
      showEditorModal.value = true
    }

    const openEdit = (script) => {
      editingScriptId.value = script.id
      scriptForm.value = {
        name: script.name || '',
        description: script.description || '',
        select_target_label:
          script.select_target_label || script.selectTargetLabel || '',
        select_target_placeholder:
          script.select_target_placeholder || script.selectTargetPlaceholder || '',
        is_active: Boolean(script.is_active),
        targets: sanitizeTargets(
          (script.targets || []).map((target) => ({
            targetType: target.targetType,
            targetId: target.targetId,
          }))
        ),
      }
      showEditorModal.value = true
    }

    const closeEditor = () => {
      showEditorModal.value = false
      resetForm()
    }

    const isSelected = (targetType, targetId) => {
      return selectedTargets.value.some(
        (target) =>
          target.targetType === targetType && Number(target.targetId) === Number(targetId)
      )
    }

    const toggleTarget = (targetType, targetId) => {
      const current = [...selectedTargets.value]
      const idx = current.findIndex(
        (target) =>
          target.targetType === targetType && Number(target.targetId) === Number(targetId)
      )
      if (idx >= 0) {
        current.splice(idx, 1)
      } else {
        current.push({ targetType, targetId: Number(targetId) })
      }
      scriptForm.value.targets = current
    }
    const moveTarget = (fromIndex, toIndex) => {
      const current = [...selectedTargets.value]
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= current.length ||
        toIndex >= current.length ||
        fromIndex === toIndex
      ) {
        return
      }
      const [moved] = current.splice(fromIndex, 1)
      current.splice(toIndex, 0, moved)
      scriptForm.value.targets = current
    }
    const moveTargetUp = (index) => moveTarget(index, index - 1)
    const moveTargetDown = (index) => moveTarget(index, index + 1)

    const saveScript = async () => {
      scriptForm.value.targets = sanitizeTargets(scriptForm.value.targets)
      if (!scriptForm.value.name.trim()) {
        showNotification(
          t('registration.embedScriptNameRequired') ||
            'Embed script name is required.',
          'error'
        )
        return
      }
      if (selectedTargets.value.length === 0) {
        showNotification(
          t('registration.embedScriptTargetRequired') ||
            'Select at least one race or cup with an assigned registration form.',
          'error'
        )
        return
      }

      try {
        if (editingScriptId.value) {
          await axios.put(`/registration/embed-script/${editingScriptId.value}`, scriptForm.value)
          showNotification(
            t('registration.embedScriptUpdated') || 'Embed script updated.',
            'success'
          )
        } else {
          await axios.post('/registration/embed-script', scriptForm.value)
          showNotification(
            t('registration.embedScriptCreated') || 'Embed script created.',
            'success'
          )
        }
        await fetchEmbedScripts()
        closeEditor()
      } catch (error) {
        console.error('Error saving embed script:', error)
        showNotification(
          error.response?.data?.error ||
            t('registration.embedScriptSaveFailed') ||
            'Failed to save embed script.',
          'error'
        )
      }
    }

    const deleteScript = async (scriptId) => {
      if (
        !confirm(
          t('registration.deleteEmbedScriptConfirm') ||
            'Are you sure you want to delete this embed script?'
        )
      ) {
        return
      }

      try {
        await axios.delete(`/registration/embed-script/${scriptId}`)
        await fetchEmbedScripts()
        showNotification(
          t('registration.embedScriptDeleted') || 'Embed script deleted.',
          'success'
        )
      } catch (error) {
        console.error('Error deleting embed script:', error)
        showNotification(
          error.response?.data?.error ||
            t('registration.embedScriptDeleteFailed') ||
            'Failed to delete embed script.',
          'error'
        )
      }
    }

    const openEmbedCode = (script) => {
      selectedScript.value = script
      embedMethod.value = 'script'
      containerId.value = 'my-registration-embed'
      showEmbedModal.value = true
    }

    const closeEmbedModal = () => {
      showEmbedModal.value = false
      selectedScript.value = null
    }

    const copyEmbedCode = async () => {
      try {
        await navigator.clipboard.writeText(generatedEmbedCode.value)
        showNotification(
          t('registration.embedCodeCopied') || 'Embed code copied.',
          'success'
        )
        closeEmbedModal()
      } catch (error) {
        console.error('Error copying embed code:', error)
        showNotification(
          t('registration.embedCodeCopyFailed') || 'Failed to copy embed code.',
          'error'
        )
      }
    }

    const targetBadgeLabel = (target) => {
      const typeLabel =
        target.targetType === 'cup'
          ? t('nav.registrationCups') || 'Cup'
          : t('nav.registrationCompetitions') || 'Competition'
      const formLabel =
        target.formTitle || target.formName || `#${target.formId || '?'}`
      return `${typeLabel}: ${target.name} -> ${formLabel}`
    }

    const competitionTargetMeta = (competition) => {
      const formName = competition.registration_form_id
        ? formNameById.value.get(competition.registration_form_id)
        : null
      return formName
        ? `${t('competitions.registrationForm')}: ${formName}`
        : t('registration.embedScriptTargetNeedsForm') ||
            'Assign a registration form first'
    }

    const cupTargetMeta = (cup) => {
      const formName = cup.registration_form_id
        ? formNameById.value.get(cup.registration_form_id)
        : null
      return formName
        ? `${t('cups.registrationForm') || 'Registration Form'}: ${formName}`
        : t('registration.embedScriptTargetNeedsForm') ||
            'Assign a registration form first'
    }

    onMounted(() => {
      fetchAll()
    })

    return {
      t,
      showNotificationFlag,
      notificationMessage,
      notificationType,
      embedScripts,
      competitions,
      cups,
      linkableCompetitions,
      linkableCups,
      orderedSelectedTargets,
      showEditorModal,
      showEmbedModal,
      editingScriptId,
      selectedScript,
      embedMethod,
      containerId,
      scriptForm,
      selectedTargets,
      generatedEmbedCode,
      openCreate,
      openEdit,
      closeEditor,
      isSelected,
      toggleTarget,
      moveTargetUp,
      moveTargetDown,
      saveScript,
      deleteScript,
      openEmbedCode,
      closeEmbedModal,
      copyEmbedCode,
      targetBadgeLabel,
      competitionTargetMeta,
      cupTargetMeta,
    }
  },
}
</script>

<style scoped>
@import './list-styles.css';

.embed-scripts-list-container {
  padding: 1rem;
  margin: 0;
  background-color: transparent;
  min-height: auto;
}

/* Empty state aligned with CompetitionList / CupList */
.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--organizer-border, #e7e5e4);
  max-width: 500px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.forms-grid .forms-table.embed-scripts-table {
  min-width: 800px;
}

.forms-table .embed-column {
  width: 1%;
  white-space: nowrap;
  text-align: center;
}

.forms-table .embed-cell {
  text-align: center;
}

.script-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.script-description {
  color: #6c757d;
  font-size: 0.84rem;
  margin-top: 4px;
  line-height: 1.3;
}

.target-list {
  margin: 0;
  padding-left: 18px;
}

.target-list-item {
  margin: 0 0 4px;
  color: #495057;
}

.muted {
  color: #6c757d;
  font-size: 0.84rem;
}

/* Buttons: neutral solids / borders (uncodixify; overrides list-styles + organizer-tokens in this view) */
.embed-scripts-list-container .list-header .btn.btn-primary,
.embed-scripts-list-container .empty-state .btn.btn-primary {
  background: #292524;
  color: #fafaf9;
  border: 1px solid #292524;
  border-radius: 8px;
  font-weight: 600;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.embed-scripts-list-container .list-header .btn.btn-primary:hover,
.embed-scripts-list-container .empty-state .btn.btn-primary:hover {
  background: #1c1917;
  border-color: #1c1917;
}

.forms-grid .form-actions .btn-action,
.forms-grid .embed-cell .btn-action {
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: #44403c;
  border: none;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.forms-grid .form-actions .btn-action:hover,
.forms-grid .embed-cell .btn-action:hover {
  background: #f5f5f4;
}

.forms-grid .form-actions .btn-action-danger {
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: #991b1b;
  border: none;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.forms-grid .form-actions .btn-action-danger:hover {
  background: #fef2f2;
}

/* Wider modals than default list-styles modal */
.modal-content.editor-modal-content {
  max-width: 980px;
  width: min(980px, 94vw);
}

.modal-content.embed-modal-content {
  max-width: 900px;
  width: min(900px, 94vw);
}

.modal-body-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-intro {
  padding-bottom: 4px;
}

.modal-intro p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
}

.modal-intro-compact h4 {
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.modal-section {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #ffffff;
}

.modal-section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #dee2e6;
  background: #f5f5f4;
}

.modal-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.modal-section-note {
  color: #6c757d;
  font-size: 12px;
  line-height: 1.4;
}

.modal-section-body {
  padding: 14px;
}

.modal-body-editor .form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-group-wide {
  grid-column: 1 / -1;
}

.modal-body-editor .form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 0;
}

.modal-section-body .form-group.inline {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.form-group-inline-field {
  max-width: 420px;
  margin-bottom: 0;
}

.modal-body-editor .form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.modal-body-editor .form-group input,
.modal-body-editor .form-group textarea {
  width: 100%;
  padding: 0.7rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.modal-body-editor .form-group input:focus,
.modal-body-editor .form-group textarea:focus {
  outline: none;
  border-color: #a8a29e;
  box-shadow: 0 0 0 2px rgba(68, 64, 60, 0.12);
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 14px;
  color: #495057;
}

.target-section {
  margin: 0;
}

.target-section-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
}

.targets-split {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.selected-targets-panel {
  margin-bottom: 16px;
  border: 1px solid #d8dee4;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.selected-targets-header {
  padding: 12px 14px;
  border-bottom: 1px solid #d8dee4;
  background: #f6f8fa;
}

.selected-targets-note {
  margin: 4px 0 0;
  color: #57606a;
  font-size: 12px;
}

.selected-targets-list {
  display: flex;
  flex-direction: column;
}

.selected-target-row {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #eef2f6;
}

.selected-target-row:last-child {
  border-bottom: none;
}

.selected-target-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #f6f8fa;
  color: #57606a;
  font-size: 12px;
  font-weight: 600;
}

.selected-target-content {
  min-width: 0;
}

.selected-target-name {
  font-size: 13px;
  font-weight: 600;
  color: #24292f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-target-meta {
  margin-top: 2px;
  color: #57606a;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-target-actions {
  display: inline-flex;
  gap: 6px;
}

.btn-order {
  min-width: 34px;
  padding: 6px 8px;
  line-height: 1;
  border: 1px solid var(--organizer-border, #e7e5e4);
  border-radius: 8px;
  background: #fff;
  color: #44403c;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.15s ease;
}

.btn-order:hover:not(:disabled) {
  background: #fafaf9;
}

.btn-order:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.target-selection-table {
  border: 1px solid #d8dee4;
  border-radius: 8px;
  overflow: hidden;
  max-height: 260px;
  overflow-y: auto;
  background: #ffffff;
}

.target-selection-head,
.target-selection-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}

.target-selection-head {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 12px;
  background: #f6f8fa;
  border-bottom: 1px solid #d8dee4;
  font-size: 12px;
  font-weight: 600;
  color: #57606a;
}

.target-selection-head span:first-child {
  width: 16px;
}

.target-selection-row {
  padding: 9px 12px;
  border-bottom: 1px solid #eef2f6;
  background: #ffffff;
}

.target-selection-row:last-child {
  border-bottom: none;
}

.target-selection-row:hover {
  background: #f6f8fa;
}

.target-selection-row.disabled {
  opacity: 0.6;
}

.target-selection-control {
  display: flex;
  align-items: center;
  justify-content: center;
}

.target-selection-name,
.target-selection-meta {
  min-width: 0;
  line-height: 1.35;
}

.target-selection-name {
  font-size: 13px;
  font-weight: 600;
  color: #24292f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.target-selection-meta {
  color: #57606a;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selection-warning {
  margin-top: 16px;
  padding: 10px 12px;
  border: 1px solid #e5c890;
  border-radius: 8px;
  background: #fff8e1;
  color: #7c5700;
  font-size: 14px;
}

.embed-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.option-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.option-row {
  min-height: 20px;
}

.code-container {
  background: #0d1117;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 8px;
  overflow: auto;
}

.code-block {
  margin: 0;
  padding: 16px;
  font-size: 13px;
  white-space: pre-wrap;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 20px;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.modal-footer .btn {
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  line-height: 1.2;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

/* Primary: solid neutral (save / copy) */
.modal-footer .modal-save-btn {
  background: #292524;
  color: #fafaf9;
  border: 1px solid #292524;
}

.modal-footer .modal-save-btn:hover {
  background: #1c1917;
  border-color: #1c1917;
}

/* Secondary: bordered (cancel) */
.modal-footer .modal-cancel-btn {
  background: #fff;
  color: #44403c;
  border: 1px solid var(--organizer-border, #e7e5e4);
}

.modal-footer .modal-cancel-btn:hover {
  background: #fafaf9;
  border-color: #d6d3d1;
}

@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
    align-items: stretch;
  }

  .form-actions,
  .embed-options,
  .modal-footer {
    flex-direction: column;
  }

  .modal-body-editor .form-grid,
  .targets-split {
    grid-template-columns: 1fr;
  }
}
</style>
